import { ChevronDown, ChevronUp } from 'lucide-react';
import { Suspense, useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';

import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';

import { AutomationsRecommendation } from './automations';
import { Card } from './card';
import { SourcingRecommendation } from './sourcing';

type RecommendationKey = 'sourcing' | 'automations';

const RECOMMENDATIONS_EXPANDED_KEY = 'dashboard-recommendations-expanded';

function RecommendationsContent() {
  const [isExpanded, setIsExpanded] = useLocalStorageState(
    RECOMMENDATIONS_EXPANDED_KEY,
    true,
  );
  const [counts, setCounts] = useState<Record<RecommendationKey, number>>(
    {} as Record<RecommendationKey, number>,
  );

  // Update the count for the given recommendation key, this is used to show the recommendation header and count if at least one of the recommendations has data
  const updateCount = useCallback((key: RecommendationKey, count: number) => {
    setCounts(prev => {
      if (prev[key] === count) return prev;
      return { ...prev, [key]: count };
    });
  }, []);

  const allLoaded = Object.keys(counts).length === 2;
  const atleastOneLoadedWithData = Object.values(counts).some(
    count => count > 0,
  );
  const recommendationCount = Object.values(counts).reduce(
    (sum, c) => sum + c,
    0,
  );

  // If all recommendations are loaded and the recommendation count is 0, don't show the recommendations. Show skeleton until all recommendations are loaded.
  if (allLoaded && recommendationCount === 0) return null;

  return (
    <div
      className={`shadow-base flex flex-col items-start gap-4 self-stretch rounded-lg bg-white p-4 ${
        !isExpanded ? 'pb-0' : ''
      }`}
    >
      <div
        className="flex w-full items-center justify-between gap-2"
        aria-expanded={isExpanded}
      >
        {/* Show the recommendation header and count if at least one of the recommendations has data */}
        {atleastOneLoadedWithData ? (
          <>
            <div className="flex items-center gap-3">
              <Typography variant="body2">
                <FormattedMessage
                  defaultMessage="Suggested actions for you"
                  description="Expandable section title for AI or rules-based recommendations"
                />
              </Typography>
              <div className="bg-fx-rose flex h-4 w-6 items-center justify-center rounded-full px-1">
                <Typography variant="small2" className="text-white">
                  {recommendationCount}
                </Typography>
              </div>
            </div>
            <button
              type="button"
              className="text-fx-neutral-600 hover:bg-fx-brand-50 flex aspect-square h-6 w-6 shrink-0 cursor-pointer items-center justify-center hover:opacity-90"
              onClick={() => setIsExpanded(prev => !prev)}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-4 w-6 rounded-full" />
          </div>
        )}
      </div>

      {/* Always mount recommendation components so they fetch and report counts for the header; only card content is toggled via isExpanded */}
      <div className="flex items-start gap-4 self-stretch">
        <Suspense fallback={isExpanded ? <Card.Skeleton /> : null}>
          <SourcingRecommendation
            isExpanded={isExpanded}
            onCountChange={c => updateCount('sourcing', c)}
          />
        </Suspense>

        <Suspense fallback={isExpanded ? <Card.Skeleton /> : null}>
          <AutomationsRecommendation
            isExpanded={isExpanded}
            onCountChange={c => updateCount('automations', c)}
          />
        </Suspense>
      </div>
    </div>
  );
}

export function Recommendations() {
  return (
    <ErrorBoundary fallback={null}>
      <RecommendationsContent />
    </ErrorBoundary>
  );
}
